// @flow
import type {
  Cohort,
  Driver,
  Location,
  Latitude,
  Longitude,
} from 'types/entities';

import React, { Component } from 'react';
import autobind from 'react-autobind';
import dimensions from 'react-dimensions';
import MapGL, { DraggablePointsOverlay } from 'react-map-gl';
import classnames from 'classnames';
import _ from 'lodash';

import { extendBounds, getBoundsZoomLevel } from 'utils/map-utils';

// Workaround for usage with flow: https://github.com/facebook/immutable-js/issues/863
import * as Immutable from 'immutable';
/* eslint-disable new-cap */ // Because of Immutable.List etc.

import './Map.scss';

import { entityURLForCohort } from '../../utils/cohort-utils';

// Defaults to IT.03SS0M9D7XP4B
const DEFAULT_LATITUDE: number = 1.28105;
const DEFAULT_LONGITUDE: number = 103.84853;
const DEFAULT_ZOOM: number = 11;
const MARKER_RADIUS: number = 15;
const pickUpMarkerPath = require('img/markers/pickup.svg');
const dropOffMarkerPath = require('img/markers/dropoff.svg');
const driverMarkerPath = require('img/taxi-types/taxi.png');

export type MapMode = 'BOOKING' | 'DISPLAY_BOOKING';

type Props = {
  accessToken: string,
  mapboxStyle: string,
  containerWidth: number,
  containerHeight: number,
  cohort?: Cohort,
  mode?: MapMode,
  mapCenterLatitude: number,
  mapCenterLongitude: number,
};

type State = {
  viewport: {
    latitude: Latitude,
    longitude: Longitude,
    zoom: number,
  },
};

function isDisplayBookingMode(mode?: MapMode): boolean {
  return !!mode && mode === 'DISPLAY_BOOKING';
}

class SimulationMap extends Component {
  static defaultProps = {
    cohort: null,
    mode: 'BOOKING',
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      viewport: {
        latitude: DEFAULT_LATITUDE,
        longitude: DEFAULT_LONGITUDE,
        zoom: DEFAULT_ZOOM,
      },
    };
    autobind(this);
  }

  state: State;

  componentDidMount() {
    document.addEventListener('dragstart', this.preventDrag, false);
  }

  componentWillReceiveProps(nextProps: Props) {
    const {
      mapCenterLatitude: newCenterLat,
      mapCenterLongitude: newCenterLng,
    } = nextProps;
    const {
      mapCenterLatitude: curCenterLat,
      mapCenterLongitude: curCenterLng,
    } = this.props;

    if (newCenterLat && newCenterLng && curCenterLat !== newCenterLat && curCenterLng !== newCenterLng) {
      this.setMapCenter(newCenterLat, newCenterLng);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('dragstart', this.preventDrag, false);
  }

  getMapCenter() {
    return {
      latitude: this.state.viewport.latitude,
      longitude: this.state.viewport.longitude,
    };
  }

  setMapCenter(latitude: Latitude, longitude: Longitude) {
    this.setState({
      viewport: {
        ...this.state.viewport,
        latitude,
        longitude,
      },
    });
  }

  preventDrag(event: Event) { // eslint-disable-line class-methods-use-this
    event.preventDefault();
  }

  zoomToFit() {
    if (this.props.cohort) {
      // Assume that there are more than one loggedIn drivers present as that is the condition
      // for the zoom-to-fit button to appear.
      const drivers = this.props.cohort.drivers;
      const driverLocations: Array<Location> = Object.keys(drivers)
                                                .map((driverID: string) => drivers[driverID])
                                                .filter((driver: Driver) => driver.loggedIn)
                                                .map((driver: Driver) =>
                                                  [driver.longitude, driver.latitude]);
      const bounds = extendBounds(driverLocations);
      const viewport = MapGL.fitBounds(this.props.containerWidth, this.props.containerHeight,
        bounds);
      const zoom: number = getBoundsZoomLevel(this.props.containerWidth, this.props.containerHeight,
        bounds);
      this.setState({
        viewport: {
          ...viewport,
          zoom,
        },
      });
    }
  }

  props: Props;

  render() {
    const viewport = { ...this.state.viewport };

    const {
      cohort,
      mode,
    } = this.props;

    let driverPoints = [];
    if (cohort && !_.isEmpty(cohort.drivers)) {
      const drivers = cohort.drivers;
      driverPoints = Object.keys(drivers)
        .map((driverID: string) => {
          const driver = drivers[driverID];
          return {
            id: driverID,
            location: [driver.longitude, driver.latitude],
            loggedIn: driver.loggedIn,
            bookings: driver.bookings,
            renderPoint: point => (
              <g
                className={classnames('entity-marker driver-marker')}
              >
                <a
                  href={`${entityURLForCohort()}/drivers/${driver.cohortDriverID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <image xlinkHref={driverMarkerPath} width="45" height="42" x="-22.5" y="-21" />
                </a>
                <text className="entity-label" textAnchor="middle">
                    Driver {point.get('id')}
                </text>
              </g>
              ),
          };
        })
        .filter(driver => driver.loggedIn);
    }

    let bookings = [];
    if (cohort && !_.isEmpty(cohort.passengers)) {
      const passengers = cohort.passengers;
      bookings = _.flatMap(passengers, (passenger) => {
        if (passenger) {
          return passenger.bookings;
        }

        return [];
      });
    }

    const bookingMarkers = [];
    _.each(bookings, (booking) => {
      const [
        pickUpMarker,
        dropOffMarker,
      ] = booking.itinerary;

      if (pickUpMarker) {
        bookingMarkers.push({
          id: 'pickUp',
          type: 'PICK_UP',
          location: [pickUpMarker.longitude, pickUpMarker.latitude],
          renderPoint: () => (
            <g
              className={classnames('entity-marker pick-up-marker', {
                disabled: isDisplayBookingMode(mode),
              })}
            >
              <a
                href={`${entityURLForCohort()}/bookings/${booking.bookingID}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <image xlinkHref={pickUpMarkerPath} width="38" height="75" x="-19" y="-37" />
              </a>
              <circle r={MARKER_RADIUS} className="pulsing-ring" />
              <text className="entity-label">
                {booking.bookingID}
              </text>
            </g>
            ),
        });
      }

      if (dropOffMarker) {
        bookingMarkers.push({
          id: 'dropOff',
          type: 'DROP_OFF',
          location: [dropOffMarker.longitude, dropOffMarker.latitude],
          renderPoint: () => (
            <g
              className={classnames('entity-marker drop-off-marker', {
                disabled: isDisplayBookingMode(mode),
              })}
            >
              <image xlinkHref={dropOffMarkerPath} width="38" height="75" x="-19" y="-37" />
              <circle r={MARKER_RADIUS} className="pulsing-ring" />
            </g>
            ),
        });
      }
    });

    const {
      containerWidth,
      containerHeight,
      accessToken,
      mapboxStyle,
    } = this.props;
    // Driver points should be the last items in the array so that
    // they are in front of other markers and will not be obscured by them.
    // onMouseUp can then trigger correctly on the driver markers without issue.
    const points = [...bookingMarkers, ...driverPoints];
    return (
      <div className="map">
        {_.size(driverPoints) > 1 &&
          <button
            className="button is-primary zoom-to-fit-button"
            title="Zoom to fit"
            onClick={this.zoomToFit}
            aria-label="Zoom map to fit markers"
          >
            <span className="icon" aria-hidden="true">
              <i className="fa fa-search-minus" />
            </span>
          </button>
        }
        <MapGL
          {...viewport}
          height={containerHeight}
          onChangeViewport={(vp) => {
            this.setState({
              viewport: vp,
            });
          }}
          mapboxApiAccessToken={accessToken}
          mapStyle={mapboxStyle}
        >
          <DraggablePointsOverlay
            {...viewport}
            mercator
            points={Immutable.fromJS(points)}
            width={containerWidth}
            height={containerHeight}
            renderPoint={point => point.get('renderPoint')(point)}
          />
        </MapGL>
      </div>
    );
  }
}

export default dimensions()(SimulationMap);



/** WEBPACK FOOTER **
 ** ./src/js/components/map/SimulationMap.jsx
 **/